import { inject, Injectable, SecurityContext } from '@angular/core';
import type { ActiveRecordsResponse } from '../models/active-records.response';
import type { ActiveRecordsModel, ActiveRecordWithColors } from '../models/active-records.model';
import { DomSanitizer } from '@angular/platform-browser';
import { getPaletteSync } from 'colorthief';
import type { DominantHexColors } from 'src/app/components/card-item/models/dominant-colors.model';
import { Util } from 'src/app/util';

@Injectable({
  providedIn: 'root',
})
export class ActiveRecordsFactory {
  private readonly sanitizer = inject(DomSanitizer);

  async buildActiveRecords(response: ActiveRecordsResponse): Promise<ActiveRecordsModel> {
    const processedRecords: ActiveRecordWithColors[] = await Promise.all(
      response.activeRecords.map(async (item) => {
        const colors = await this.getColorFromImage(item.coverUrl);

        return {
          ...item,
          progress: Math.trunc(item.progress),
          record: Util.prepareRecord(item.record),
          issueDate: Util.getLastIssueDate(item.record),
          dominantColors: colors,
          coverDetails: {
            url: item.coverUrl || item.record.coverImageUrl,
            icon: item.record.icon,
            isList: Boolean(item.record.wishListInfo),
          },
        };
      })
    );

    return {
      activeRecords: processedRecords,
      canMakeCheckouts: response.canMakeCheckouts,
    };
  }

  private async getColorFromImage(imageUrl: string): Promise<DominantHexColors | null> {
    const sanitizedUrl = imageUrl ? this.sanitizer.sanitize(SecurityContext.URL, imageUrl) : 'assets/cover.jpg';
    let img: HTMLImageElement | null = null;

    try {
      img = await this.loadImage(sanitizedUrl || 'assets/cover.jpg');
    } catch {
      img = await this.loadImage('assets/cover.jpg').catch(() => null);
    }

    if (!img) {
      return null;
    }

    const palette = getPaletteSync(img, { colorCount: 5 });

    if (!palette?.length) {
      return null;
    }

    return {
      showGradient: true,
      dominant: palette[0].array(),
      secondary: (palette[1] ?? palette[0]).array(),
    };
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.src = src;
      img.onload = () => resolve(img);
      img.onerror = () => reject();
    });
  }
}
